import { useEffect, useMemo, useState, type FormEvent } from "react";
import { Link, useSearchParams } from "react-router-dom";
import PageMeta from "@/components/feature/PageMeta";
import Breadcrumb from "@/components/feature/Breadcrumb";
import MediaGrid from "@/components/feature/MediaGrid";
import Pagination from "@/components/feature/Pagination";
import EmptyState from "@/components/feature/EmptyState";
import SectionHeader from "@/components/feature/SectionHeader";
import {
  searchMedia,
  sortByPopular,
  mediaList,
  paginate,
  totalPages,
  PAGE_SIZE,
} from "@/lib/media";
import { searchNews, NEWS_PAGE_SIZE } from "@/lib/news";

type Tab = "media" | "news";

export default function Search() {
  const [params, setParams] = useSearchParams();
  const keyword = params.get("q") ?? "";
  const [input, setInput] = useState(keyword);
  const [tab, setTab] = useState<Tab>("media");
  const [mediaPage, setMediaPage] = useState(1);
  const [newsPage, setNewsPage] = useState(1);

  useEffect(() => {
    setInput(keyword);
    setMediaPage(1);
    setNewsPage(1);
  }, [keyword]);

  const mediaResults = useMemo(() => searchMedia(keyword), [keyword]);
  const newsResults = useMemo(() => searchNews(keyword), [keyword]);

  const mediaTotal = totalPages(mediaResults.length, PAGE_SIZE);
  const mediaCurrent = Math.min(mediaPage, mediaTotal);
  const mediaPaged = paginate(mediaResults, mediaCurrent, PAGE_SIZE);

  const newsTotal = Math.max(1, Math.ceil(newsResults.length / NEWS_PAGE_SIZE));
  const newsCurrent = Math.min(newsPage, newsTotal);
  const newsPaged = newsResults.slice(
    (newsCurrent - 1) * NEWS_PAGE_SIZE,
    newsCurrent * NEWS_PAGE_SIZE
  );

  const recommendations = sortByPopular(mediaList).slice(0, 4);

  const submit = (e: FormEvent) => {
    e.preventDefault();
    const q = input.trim();
    if (q) setParams({ q });
    else setParams({});
  };

  const hasKeyword = keyword.trim().length > 0;
  const noResults = hasKeyword && mediaResults.length === 0 && newsResults.length === 0;

  return (
    <>
      <PageMeta
        title={hasKeyword ? `“${keyword}”的搜索结果 - 蜜桃成熟` : "搜索影视作品与资讯 - 蜜桃成熟"}
        description="在蜜桃成熟搜索电影、电视剧与影视资讯，快速定位你想了解的作品。"
      />

      <div className="w-full px-4 md:px-8 py-6 md:py-8">
        <Breadcrumb items={[{ label: "首页", to: "/" }, { label: "搜索" }]} />

        <div className="mt-5 mb-8">
          <h1 className="text-2xl md:text-3xl font-heading font-semibold text-foreground-950">
            搜索影视作品与资讯
          </h1>
          <form
            onSubmit={submit}
            className="flex items-center gap-2 mt-5 max-w-2xl bg-background-50 border border-background-200 rounded-full pl-4 pr-1.5 py-1.5 focus-within:border-primary-400 transition-colors"
          >
            <span className="w-5 h-5 flex items-center justify-center text-foreground-400">
              <i className="ri-search-line"></i>
            </span>
            <input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="输入片名、类型、地区或资讯关键词"
              className="flex-1 min-w-0 bg-transparent outline-none text-sm text-foreground-900 placeholder:text-foreground-400"
            />
            <button
              type="submit"
              className="px-5 py-2 rounded-full bg-primary-500 hover:bg-primary-600 text-background-50 text-sm whitespace-nowrap transition-colors cursor-pointer"
            >
              搜索
            </button>
          </form>
          {hasKeyword && (
            <p className="text-sm text-foreground-500 mt-4">
              当前关键词：
              <span className="text-primary-600 font-medium mx-1">{keyword}</span>
              ，共找到 {mediaResults.length} 部作品、{newsResults.length} 条资讯
            </p>
          )}
        </div>

        {!hasKeyword ? (
          <div>
            <EmptyState
              icon="ri-search-eye-line"
              title="输入关键词开始搜索"
              description="试试搜索片名、演员、类型或影视资讯，例如「悬疑」「电视剧」「山海之约」。"
            />
            <div className="mt-12">
              <SectionHeader title="大家都在看" accent />
              <MediaGrid items={recommendations} />
            </div>
          </div>
        ) : noResults ? (
          <div>
            <EmptyState
              icon="ri-emotion-sad-line"
              title={`没有找到与“${keyword}”相关的内容`}
              description="换个关键词试试，或者看看下面这些热门推荐。"
            />
            <div className="mt-12">
              <SectionHeader title="为你推荐" accent />
              <MediaGrid items={recommendations} />
            </div>
          </div>
        ) : (
          <>
            <div className="flex items-center gap-2 mb-8">
              <button
                type="button"
                onClick={() => setTab("media")}
                className={`px-4 py-2 rounded-full text-sm border whitespace-nowrap transition-colors cursor-pointer ${
                  tab === "media"
                    ? "bg-primary-500 border-primary-500 text-background-50"
                    : "bg-background-50 border-background-200 text-foreground-600 hover:border-primary-300 hover:text-primary-600"
                }`}
              >
                影视作品 ({mediaResults.length})
              </button>
              <button
                type="button"
                onClick={() => setTab("news")}
                className={`px-4 py-2 rounded-full text-sm border whitespace-nowrap transition-colors cursor-pointer ${
                  tab === "news"
                    ? "bg-primary-500 border-primary-500 text-background-50"
                    : "bg-background-50 border-background-200 text-foreground-600 hover:border-primary-300 hover:text-primary-600"
                }`}
              >
                影视资讯 ({newsResults.length})
              </button>
            </div>

            {tab === "media" ? (
              mediaPaged.length > 0 ? (
                <>
                  <MediaGrid items={mediaPaged} showSynopsis />
                  <div className="mt-10">
                    <Pagination page={mediaCurrent} totalPages={mediaTotal} onChange={setMediaPage} />
                  </div>
                </>
              ) : (
                <EmptyState
                  icon="ri-film-line"
                  title="没有匹配的影视作品"
                  description="切换标签看看资讯结果。"
                  actionLabel="查看资讯结果"
                  onAction={() => setTab("news")}
                />
              )
            ) : newsPaged.length > 0 ? (
              <>
                <div className="flex flex-col gap-4">
                  {newsPaged.map((item) => (
                    <Link
                      key={item.id}
                      to={`/news/detail/${item.id}`}
                      className="group flex flex-col sm:flex-row gap-4 p-4 md:p-5 rounded-lg border border-background-200 bg-background-50 hover:border-primary-300 transition-colors"
                    >
                      <div className="w-full sm:w-48 h-36 shrink-0 rounded-md overflow-hidden bg-background-100">
                        <img
                          src={item.image}
                          alt={item.title}
                          title={`${item.title} 资讯`}
                          className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                        />
                      </div>
                      <div className="min-w-0 flex-1">
                        <span className="text-xs px-2 py-0.5 rounded-md bg-accent-100 text-accent-800">
                          {item.category}
                        </span>
                        <h2 className="text-base font-medium text-foreground-950 leading-snug mt-3 line-clamp-2 group-hover:text-primary-700 transition-colors">
                          {item.title}
                        </h2>
                        <p className="text-sm text-foreground-600 leading-relaxed mt-2 line-clamp-2">
                          {item.summary}
                        </p>
                        <p className="text-xs text-foreground-400 mt-3">{item.publishTime}</p>
                      </div>
                    </Link>
                  ))}
                </div>
                {newsTotal > 1 && (
                  <div className="mt-10">
                    <Pagination page={newsCurrent} totalPages={newsTotal} onChange={setNewsPage} />
                  </div>
                )}
              </>
            ) : (
              <EmptyState
                icon="ri-newspaper-line"
                title="没有匹配的资讯"
                description="切换标签看看影视作品结果。"
                actionLabel="查看影视作品"
                onAction={() => setTab("media")}
              />
            )}
          </>
        )}
      </div>
    </>
  );
}